<template>
  <div class="ethereum-details-layout">
    <!-- Details Accordion -->
    <TransactionDetailAccordion
      v-if="isDetailsAccordion"
      :data="formattedDetailsData"
      :isLoading="isLoading"
    />
    <!-- Combined Transaction Block -->
    <div class="transaction-block">
      <!-- Sender -->
      <AddressBlockEthereum
        :addressFrom="details?.from || ''"
        :addressTo="details?.to || ''"
        :amount="details?.value?.amount || details?.token_transfer?.amount"
        :decimals="
          details?.value?.decimals || details?.token_transfer?.decimals
        "
        :symbol="
          details?.value?.symbol || details?.token_transfer?.symbol || ''
        "
        :usd_value="
          details?.value?.usd_value || details?.token_transfer?.usd_value || 0
        "
        :details="details"
        :token_verified="details?.token_verified"
        :network="network"
        :showArrow="false"
        :isLoading="isLoading"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
// Use common components with full path alias
import TransactionDetailAccordion from "@/features/TransactionDetails/Common/ui/TransactionDetailAccordion.vue";
import AddressBlockEthereum from "./AddressBlockEthereum.vue";
// Removed date-fns format import
// Import useLiveAge composable instead of getAge
import { useLiveAge } from "@/shared/composables/useLiveAge";
import { formatNumberByThousands } from "@/shared/utils/formaters/transactionFormatter";
import { useI18n } from "vue-i18n";

const props = defineProps({
  details: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  block: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  fee: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  network: {
    type: String,
    required: true,
  },
  status: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
  isDetailsAccordion: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const { locale } = useI18n();
console.log(props.details, "det");
const formattedFee = computed(() => {
  if (!props.fee || !props.fee.amount) return "-";
  const amount = props.fee.amount;
  const symbol = props.fee.symbol.toUpperCase() || "";
  const usdString =
    props.fee.usd_value && props.fee.usd_value !== "0.00"
      ? `$\u202F${props.fee.usd_value}`
      : "";
  return `${amount} ${symbol} ${usdString}`.trim();
});

// Use the new live age composable
const timestampComputed = computed(() => props.block?.timestamp);

const { age: formattedAge } = useLiveAge(timestampComputed, locale);

// Revert computed property for Accordion data to output object values
const formattedDetailsData = computed(() => {
  const contractAddress = props.details?.contract_address;
  const contractHref = contractAddress
    ? `/explorer/${props.network}/address/${contractAddress}`
    : null;

  // Format token type - remove 'tron_' prefix and convert to uppercase
  const tokenType = props.details?.tx_type
    ? props.details.tx_type.replace("tron_", "").toUpperCase()
    : null;

  return [
    {
      id: "block",
      label: "Block ID",
      value: { title: props.block?.number || "-" },
    },
    // Format Age into the value.title structure
    {
      id: "age",
      label: "Age",
      value: {
        title:
          locale?.value !== "en"
            ? `${formattedAge.value} назад`
            : `${formattedAge.value} ago`,
      },
    },
    // Add transaction status if available
    ...(props.status?.confirmed !== undefined
      ? [
          {
            id: "status",
            label: "Status",
            value: {
              title: props.status.confirmed ? "Confirmed" : "Unconfirmed",
              confirmationCount: formatNumberByThousands(
                props.status.confirmations,
              ),
              // totalConfirmations: props.status.confirmations,
            },
          },
        ]
      : []),
    {
      id: "energy",
      label: "Energy Used",
      value: { title: props.details?.energy_used?.toLocaleString() || "-" },
    },
    // Add token type if it exists
    ...(tokenType
      ? [
          {
            id: "token_type",
            label: "Token Type",
            value: { title: tokenType },
          },
        ]
      : []),
    // Use object structure for contract address, keep copy flag
    {
      id: "contract",
      label: "Contract",
      value: { title: contractAddress || "-", href: contractHref },
    },
    {
      id: "fee",
      label: "Fee",
      value: { title: formattedFee.value },
    },
  ].filter((item) => item.value.title !== "-"); // Filter based on title
});
</script>

<style scoped lang="scss">
.ethereum-details-layout {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* New styles for the combined transaction block */
.transaction-block {
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--Dark-850, #1a1a1a);
  background: var(--Dark-900, #111);
  padding: 32px;
}

.separator {
  border: none;
  border-top: 1px solid var(--Dark-850, #1a1a1a);
  margin: 24px 0; // Adjust spacing as needed
}

/* Remove styles that are no longer needed */
/* .address-block-wrapper { ... } */
/* .amount-item { ... } */

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
